import {
  Text,
  View,
  type StyleProp,
  type TextStyle,
  type ViewStyle,
} from "react-native";
import type { LucideIcon } from "lucide-react-native";
import type { ReactNode } from "react";

export type TagVariant = "soft" | "outline";
export type TagTone =
  | "neutral"
  | "gold"
  | "coral"
  | "green"
  | "orange"
  | "blue"
  | "purple"
  | "chatNeutral"
  | "sourceBlue"
  | "sourcePurple"
  | "sourceGreen";
export type TagShape = "rounded" | "pill";

type TagColorSet = {
  text: string;
  bg: string;
};

const ROUNDED_RADIUS = 6;
const OUTLINE_RADIUS = 8;

const SOFT_TONE_COLORS: Record<TagTone, TagColorSet> = {
  neutral: { text: "#6B5548", bg: "rgba(139, 111, 94, 0.1)" },
  gold: { text: "#8A5A24", bg: "rgba(245, 200, 130, 0.26)" },
  coral: { text: "#9E3F26", bg: "rgba(232, 146, 124, 0.18)" },
  green: { text: "#4E7F61", bg: "rgba(130, 212, 168, 0.18)" },
  orange: { text: "#9A4D19", bg: "rgba(232, 146, 44, 0.18)" },
  blue: { text: "#4E638F", bg: "rgba(123, 142, 181, 0.18)" },
  purple: { text: "#72528F", bg: "rgba(151, 116, 181, 0.18)" },
  chatNeutral: { text: "#707070", bg: "rgba(112, 112, 112, 0.08)" },
  sourceBlue: { text: "#3B82F6", bg: "rgba(59, 130, 246, 0.1)" },
  sourcePurple: { text: "#8B5CF6", bg: "rgba(139, 92, 246, 0.1)" },
  sourceGreen: { text: "#4E7F61", bg: "rgba(130, 212, 168, 0.18)" },
};

const OUTLINE_TONE_COLORS: Record<TagTone, TagColorSet> = {
  neutral: { text: "#8A7D74", bg: "transparent" },
  gold: { text: "#8A5A24", bg: "transparent" },
  coral: { text: "#9E3F26", bg: "transparent" },
  green: { text: "#4E7F61", bg: "transparent" },
  orange: { text: "#9A4D19", bg: "transparent" },
  blue: { text: "#4E638F", bg: "transparent" },
  purple: { text: "#72528F", bg: "transparent" },
  chatNeutral: { text: "#707070", bg: "transparent" },
  sourceBlue: { text: "#3B82F6", bg: "transparent" },
  sourcePurple: { text: "#8B5CF6", bg: "transparent" },
  sourceGreen: { text: "#4E7F61", bg: "transparent" },
};

const VARIANT_TONE_COLORS: Record<TagVariant, Record<TagTone, TagColorSet>> = {
  soft: SOFT_TONE_COLORS,
  outline: OUTLINE_TONE_COLORS,
};

export type TagProps = {
  label: string;
  variant?: TagVariant;
  tone?: TagTone;
  icon?: LucideIcon;
  leading?: ReactNode;
  trailing?: ReactNode;
  shape?: TagShape;
  iconOnly?: boolean;
  accessibilityLabel?: string;
  style?: StyleProp<ViewStyle>;
  textStyle?: StyleProp<TextStyle>;
};

export function Tag({
  label,
  variant = "soft",
  tone = "neutral",
  icon: Icon,
  leading,
  trailing,
  shape = "rounded",
  iconOnly = false,
  accessibilityLabel,
  style,
  textStyle,
}: TagProps) {
  const toneColors = VARIANT_TONE_COLORS[variant][tone];
  const isOutline = variant === "outline";
  const radius =
    shape === "pill" ? 999 : isOutline ? OUTLINE_RADIUS : ROUNDED_RADIUS;

  return (
    <View
      accessibilityLabel={accessibilityLabel ?? label}
      style={[
        {
          flexDirection: "row",
          alignItems: "center",
          gap: isOutline ? 5 : 4,
          minHeight: isOutline ? undefined : 18,
          paddingHorizontal: iconOnly ? 4 : isOutline ? 10 : 8,
          paddingVertical: iconOnly ? 2 : isOutline ? 5 : 3,
          borderRadius: radius,
          borderWidth: isOutline ? 1 : 0,
          borderColor: isOutline ? "rgba(0,0,0,0.06)" : "transparent",
          backgroundColor: toneColors.bg,
        },
        style,
      ]}
    >
      {leading ??
        (Icon ? (
          <Icon
            size={iconOnly ? 12 : isOutline ? 10 : 11}
            color={toneColors.text}
            strokeWidth={isOutline ? 1.5 : 1.8}
          />
        ) : null)}
      {iconOnly ? null : (
        <Text
          className="font-dm-sans"
          style={[
            {
              fontSize: 11,
              lineHeight: 14,
              fontWeight: isOutline ? "500" : "600",
              color: toneColors.text,
              letterSpacing: isOutline ? 0.2 : 0,
            },
            textStyle,
          ]}
        >
          {label}
        </Text>
      )}
      {trailing}
    </View>
  );
}

export default Tag;
